import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { Container, Stack, Typography } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

import HeaderBreadcrumbs from "@components/HeaderBreadcrumbs";
import Page from "@components/Page";
import { TableFilterWrapper } from "@components/filter-table-wrapper";
import useLocales from "@hooks/useLocales";
import { PATH_DASHBOARD } from "@routes/paths";

import { getProcurementPurchaseOrderList } from "../../services/purchaseOrderService";
import {
  IPurchaseOrderListItem,
  IPurchaseOrderListParams,
} from "../../types/purchaseOrderTypes";
import {
  IPurchaseOrderFilterState,
  PurchaseOrderDraftFilters,
} from "./PurchaseOrderDraftFilters";
import { PurchaseOrderDraftTable } from "./PurchaseOrderDraftTable";

const DEFAULT_ROWS_PER_PAGE = 10;

export const PurchaseOrderDrafts = () => {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<IPurchaseOrderListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [filterState, setFilterState] = useState<IPurchaseOrderFilterState>({
    poNumber: "",
    vendorCompanyName: "",
    customerCompanyName: "",
  });

  const fetchList = async (
    currentPage: number,
    currentRowsPerPage: number,
    filters?: IPurchaseOrderFilterState,
  ) => {
    setLoading(true);
    try {
      const params: IPurchaseOrderListParams = {
        page: currentPage,
        record: currentRowsPerPage,
        poNumber: filters?.poNumber,
        vendorCompanyName: filters?.vendorCompanyName,
        customerCompanyName: filters?.customerCompanyName,
        isSaveAsDraft: true,
      };

      const { data, error, forbidden } = await getProcurementPurchaseOrderList(
        params,
      );

      if (forbidden) {
        enqueueSnackbar(translate("permission_denied"), { variant: "error" });
        setRows([]);
        setHasNextPage(false);
      } else if (error || !data) {
        enqueueSnackbar(translate(error || "something_went_wrong"), {
          variant: "error",
        });
        setRows([]);
        setHasNextPage(false);
      } else {
        setRows(data.purchaseOrders || []);
        setHasNextPage(data.hasNextPage);
      }
    } catch (error) {
      enqueueSnackbar(translate("something_went_wrong"), { variant: "error" });
      setRows([]);
      setHasNextPage(false);
    }
    setLoading(false);
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage + 1);
    fetchList(newPage + 1, rowsPerPage, filterState);
  };

  const handleRowsPerPageChange = (
    event: SelectChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value as string, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    fetchList(1, newRowsPerPage, filterState);
  };

  useEffect(() => {
    setPage(1);
    fetchList(1, rowsPerPage, filterState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);

  return (
    <Page title={translate("procurement")}>
      <Container maxWidth={false}>
        <HeaderBreadcrumbs
          links={[
            { name: translate("Dashboard"), href: PATH_DASHBOARD.root },
            { name: translate("procurement") },
            { name: translate("purchase_order_drafts") },
          ]}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3, mt: 1 }}
        >
          <Typography variant="h3" fontWeight="bold" textTransform="capitalize">
            {translate("purchase_order_drafts")}
          </Typography>
        </Stack>

        <TableFilterWrapper>
          <TableFilterWrapper.Header>
            <PurchaseOrderDraftFilters
              loader={loading}
              filterState={filterState}
              setFilterState={setFilterState}
            />
          </TableFilterWrapper.Header>
          <TableFilterWrapper.Content>
            <PurchaseOrderDraftTable
              rows={rows}
              loading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              hasNextPage={hasNextPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </TableFilterWrapper.Content>
        </TableFilterWrapper>
      </Container>
    </Page>
  );
};
